// ============================================================================
// styleToCSS — Convert LayoutElementStyle to React.CSSProperties (F12.B T12.B.6)
// Skopiowane i zaadaptowane z SellCall (uproszczone — bez Google Fonts, bez framer-motion)
// ============================================================================

import type { LayoutElementStyle } from './styleTypes';

function addAlpha(hex: string, alpha: number): string {
  if (hex.startsWith('rgba')) {
    return hex.replace(/,\s*[\d.]+\)/, `, ${alpha})`);
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Convert LayoutElementStyle into React.CSSProperties.
 * Używane w ModuleBuilder (podgląd na canvasie) i RecordFormModal (render pola).
 *
 * UWAGA: zwraca PEŁEN CSS (container + text). Jeśli renderowanie ma Tailwind classes
 * z `text-text-primary` na inner elemencie, to color z tego CSS NIE zadziała — bo
 * class na childu nadpisuje inherited. W takim przypadku użyj `styleToCSSSplit()`
 * i zaaplikuj `text` na inner element, `container` na wrapper.
 */
export function styleToCSS(rawStyle: LayoutElementStyle | undefined): React.CSSProperties {
  const split = styleToCSSSplit(rawStyle);
  return { ...split.container, ...split.text };
}

/**
 * Convert LayoutElementStyle into TWO React.CSSProperties:
 *  - `container` — bg/border/padding/margin/opacity (idą na wrapper div)
 *  - `text`      — color/font/lineHeight/letterSpacing/decoration/transform/align
 *                  (idą na inner element renderujący tekst — żeby Tailwind classes
 *                  typu `text-text-primary` nie nadpisywały inherited color)
 *
 * Używane gdy renderowanie ma wewnętrzne komponenty z własnymi text-color classes
 * (RecordDetailPage ViewField, RecordFormModal FieldRow, ModuleBuilderPage canvas).
 */
export function styleToCSSSplit(rawStyle: LayoutElementStyle | undefined): {
  container: React.CSSProperties;
  text: React.CSSProperties;
} {
  if (!rawStyle) return { container: {}, text: {} };
  const s = rawStyle;
  const container: React.CSSProperties = {};
  const text: React.CSSProperties = {};

  // 1. Typografia → idzie na inner text element
  if (s.fontSize) text.fontSize = `${s.fontSize}px`;
  if (s.fontWeight) text.fontWeight = s.fontWeight;
  if (s.lineHeight) text.lineHeight = s.lineHeight;
  if (s.letterSpacing !== undefined && s.letterSpacing !== 0)
    text.letterSpacing = `${s.letterSpacing}px`;
  if (s.textDecoration && s.textDecoration !== 'none') text.textDecoration = s.textDecoration;
  if (s.textTransform && s.textTransform !== 'none') text.textTransform = s.textTransform;
  if (s.textAlign) text.textAlign = s.textAlign;
  if (s.textColor) {
    text.color =
      s.textOpacity !== undefined && s.textOpacity < 100
        ? addAlpha(s.textColor, s.textOpacity / 100)
        : s.textColor;
  }

  // 2. Wypełnienie → wrapper
  if (s.backgroundColor) {
    container.backgroundColor =
      s.backgroundOpacity !== undefined && s.backgroundOpacity < 100
        ? addAlpha(s.backgroundColor, s.backgroundOpacity / 100)
        : s.backgroundColor;
    container.backgroundImage = 'none';
  }

  // 3. Obramowanie → wrapper
  if (s.borderWidth) {
    container.borderWidth = `${s.borderWidth}px`;
    container.borderStyle = s.borderStyle ?? 'solid';
    container.borderColor = s.borderColor ?? '#E5E7EB';
  }
  if (s.borderRadius !== undefined) container.borderRadius = `${s.borderRadius}px`;

  // 4. Efekty → wrapper
  if (s.opacity !== undefined && s.opacity < 100) container.opacity = s.opacity / 100;

  // 5. Odstępy → wrapper
  if (s.paddingTop !== undefined) container.paddingTop = `${s.paddingTop}px`;
  if (s.paddingRight !== undefined) container.paddingRight = `${s.paddingRight}px`;
  if (s.paddingBottom !== undefined) container.paddingBottom = `${s.paddingBottom}px`;
  if (s.paddingLeft !== undefined) container.paddingLeft = `${s.paddingLeft}px`;
  if (s.marginTop !== undefined) container.marginTop = `${s.marginTop}px`;
  if (s.marginBottom !== undefined) container.marginBottom = `${s.marginBottom}px`;

  return { container, text };
}
